import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { fetchDepartmentsPaginated, getEmployeePositions, updateUser } from 'services/registration';
import { schemaUpdateUser } from './validation';
import { useTranslation } from 'react-i18next';
import { PATHS } from 'constants/routes';
import { useNavigate } from 'react-router-dom';
import { getCourseData } from 'services/elearning';

export interface UpdateUserRequest {
    first_name: string;
    last_name: string;
    phone?: string;
    workplace_name: string;
    workplace_address_city: string;
    workplace_address_street: string;
    workplace_address_number: string;
    workplace_address_postcode: string;
    employee_position_id?: number | null;
    department_id?: number;
    email: string;
    workplace_id: number;
}

export const useProfilUpdate = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userData = (() => {
        const raw = localStorage.getItem('userData');
        return raw ? JSON.parse(raw) : {};
    })();
    
    const {
        register: registerUpdate,
        handleSubmit: handleSubmitUpdate,
        formState: { errors: updateErrors },
        setError: setErrorUpdate,
        reset,
        control,
        setValue: setValueUpdate,
    } = useForm<UpdateUserRequest>({
        resolver: yupResolver(schemaUpdateUser),
        defaultValues: {
            first_name: userData?.first_name || '',
            last_name: userData?.last_name || '',
            phone: userData?.phone || '000000000',
            workplace_name: userData?.workplace_name || '',
            workplace_address_city: userData?.workplace_address_city || '',
            workplace_address_street: userData?.workplace_address_street || '',
            workplace_address_number: userData?.workplace_address_number || '',
            workplace_address_postcode: userData?.workplace_address_postcode || '',
            employee_position_id: userData?.employee_position_id ?? null,
            department_id: userData?.department_id || '',
            email: userData?.email || '',
            workplace_id: userData?.workplace_id,
        },
    });

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(
        userData?.department_id && userData?.department
            ? { label: userData.department, value: userData.department_id }
            : null
    );

    const [selectedPosition, setSelectedPosition] = useState<{ label: string; value: number } | null>(null);

    useEffect(() => {
        if (userData?.employee_position_id) {
            getEmployeePositions(1).then(res => {
                const match = res.data.find(p => p.id === userData.employee_position_id);
                if (match) {
                    setSelectedPosition({ label: match.name, value: match.id });
                    setValueUpdate('employee_position_id', match.id);
                }
            });
        }
    }, []);

    const loadDepartmentOptions = async (search: string, _: any, { page }: { page: number }) => {
        if (!userData?.workplace_id) return { options: [], hasMore: false, additional: { page: 1 } };
        const res = await fetchDepartmentsPaginated(userData.workplace_id, page);
        const options = res.data.map((d: any) => ({ label: d.name, value: d.id }));
        if (page === 1 && userData?.department_id && !selectedDepartment) {
            const match = options.find((o: any) => o.value === userData.department_id);
            if (match) {
                setSelectedDepartment(match);
                setValueUpdate('department_id', match.value);
            }
        }
        return {
            options,
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handleDepartmentChange = (option: { label: string; value: number } | null) => {
        setSelectedDepartment(option);
        setValueUpdate('department_id', option?.value ?? null);
    };

    const loadPositionOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getEmployeePositions(page, search || undefined);
        return {
            options: res.data.map((p) => ({ label: p.name, value: p.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const handlePositionChange = (option: { label: string; value: number } | null) => {
        setSelectedPosition(option);
        setValueUpdate('employee_position_id', option?.value ?? null);
    };

    const mutationUpdate = useMutation(updateUser, {
        onSuccess: async (res) => {
            toast.success(t('registration.successUpdated'));
            reset(res.data);
            const coursesRes = await getCourseData().catch(() => null);
            const firstCoursePath = coursesRes?.data?.[0]?.path;
            const redirectPath = firstCoursePath ? `${PATHS.ONLINE_TRAININGS}/${firstCoursePath}` : PATHS.HAND_HYGIENE;
            navigate(redirectPath);
        },
        onError: (error: any) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setErrorUpdate(key as keyof UpdateUserRequest, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const onSubmitUpdate = ({ department_id, ...rest }: UpdateUserRequest) => {
        const payload: any = { ...rest };
        if (department_id != null) payload.department_id = department_id;
        mutationUpdate.mutate(payload);
    };

    return {
        registerUpdate,
        handleSubmitUpdate,
        setErrorUpdate,
        onSubmitUpdate,
        updateErrors,
        t,
        control,
        setValueUpdate,
        selectedDepartment,
        loadDepartmentOptions,
        handleDepartmentChange,
        selectedPosition,
        loadPositionOptions,
        handlePositionChange,
    };
};